import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Heart,
    Star,
    Sparkles,
    Crown,
    Zap,
    Sun,
    ChevronRight,
    BookOpen,
    RefreshCw
} from 'lucide-react';
import { fetchDailyDeclarations } from '../../api/adminService';
import ImagesDev from '../../api/images';

interface Declaration {
    id: number;
    date: string;
    shortWord: string;
    bibleReference: string;
    declarations: string[];
}

const DailyDeclarations = () => {
    const [todaysDeclaration, setTodaysDeclaration] = useState<Declaration | null>(null);
    const [currentDeclarationIndex, setCurrentDeclarationIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [bgImage, setBgImage] = useState("");

    useEffect(() => {
        setBgImage(getRandomHeroImage())
    }, [])

    useEffect(() => {
        const fetchDeclarations = async () => {
            try {
                setLoading(true);
                const items = await fetchDailyDeclarations();
                setTodaysDeclaration(items[0]);
                setError(null);
            } catch (err) {
                setError('Failed to fetch declarations');
                console.error('Error fetching declarations:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchDeclarations();
    }, []);

    useEffect(() => {
        if (!todaysDeclaration?.declarations) return;

        const interval = setInterval(() => {
            setCurrentDeclarationIndex(prev =>
                (prev + 1) % todaysDeclaration.declarations.length
            );
        }, 10000);

        return () => clearInterval(interval);
    }, [todaysDeclaration]);

    const handleRefresh = async () => {
        const fetchDeclarations = async () => {
            try {
                setLoading(true);
                const items = await fetchDailyDeclarations();
                setTodaysDeclaration(items[0]);
                setCurrentDeclarationIndex(0);
                setError(null);
            } catch (err) {
                setError('Failed to fetch declarations');
                console.error('Error fetching declarations:', err);
            } finally {
                setLoading(false);
            }
        };

        await fetchDeclarations();
    };

    const getRandomHeroImage = () => {
        const images = window.innerWidth > 768 ? ImagesDev.heroImagesDesk : ImagesDev.heroImages;
        return images[Math.floor(Math.random() * images.length)];
    };


    if (loading) {
        return (
            <main className="min-h-screen flex justify-center items-center px-4 py-12 relative overflow-hidden">
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
                    <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/1261728/pexels-photo-1261728.jpeg')] bg-cover bg-center opacity-20"></div>
                    <motion.img
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.4 }}
                        transition={{ duration: 2 }}
                        className='absolute top-0 left-0 h-full w-full'
                        src={bgImage}
                        style={{ objectFit: "cover" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="relative z-10 text-center"
                >
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        className="inline-block"
                    >
                        <Sparkles className="h-16 w-16 text-yellow-400 mb-4" />
                    </motion.div>
                    <p className="text-white text-xl font-semibold">Loading Today's Declaration...</p>
                </motion.div>
            </main>
        );
    }

    if (error || !todaysDeclaration) {
        return (
            <main className="min-h-screen flex justify-center items-center px-4 py-12 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-purple-900 to-indigo-900">
                    {/* <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/1261728/pexels-photo-1261728.jpeg')] bg-cover bg-center opacity-20"></div> */}
                    <motion.img
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.4 }}
                        transition={{ duration: 2 }}
                        className='absolute top-0 left-0 h-full w-full'
                        src={bgImage}
                        style={{ objectFit: "cover" }}
                    />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative z-10 text-center bg-black/40 backdrop-blur-lg rounded-2xl p-8"
                >
                    <Zap className="h-16 w-16 text-red-400 mx-auto mb-4" />
                    <p className="text-white text-xl font-semibold mb-4">{error || 'No declarations available'}</p>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleRefresh}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 mx-auto"
                    >
                        <RefreshCw className="h-5 w-5" />
                        Try Again
                    </motion.button>
                </motion.div>
            </main>
        );
    }

    const currentDeclaration = todaysDeclaration.declarations[currentDeclarationIndex];

    return (
        <main className="min-h-screen flex justify-center items-center px-4 py-12 relative overflow-hidden">
            {/* Dynamic Background with Parallax Effect */}
            <motion.div
                className="absolute inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900"
                animate={{
                    background: [
                        "linear-gradient(to bottom right, #581c87, #1e3a8a, #312e81)",
                        "linear-gradient(to bottom right, #7c3aed, #3b82f6, #6366f1)",
                        "linear-gradient(to bottom right, #581c87, #1e3a8a, #312e81)"
                    ]
                }}
                transition={{ duration: 10, repeat: Infinity }}
            >
                {/* <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/1261728/pexels-photo-1261728.jpeg')] bg-cover bg-center opacity-30"></div> */}
                <motion.img
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.4 }}
                    transition={{ duration: 2 }}
                    className='absolute top-0 left-0 h-full w-full'
                    src={bgImage}
                    style={{ objectFit: "cover" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            </motion.div>

            {/* Floating Elements */}
            <motion.div
                animate={{
                    y: [-20, 20, -20],
                    rotate: [0, 5, 0, -5, 0]
                }}
                transition={{ duration: 8, repeat: Infinity }}
                className="absolute top-20 left-10 hidden lg:block"
            >
                <Star className="h-8 w-8 text-yellow-400/70" />
            </motion.div>

            <motion.div
                animate={{
                    y: [20, -20, 20],
                    rotate: [0, -5, 0, 5, 0]
                }}
                transition={{ duration: 6, repeat: Infinity, delay: 1 }}
                className="absolute top-32 right-16 hidden lg:block"
            >
                <Crown className="h-10 w-10 text-amber-400/70" />
            </motion.div>

            <motion.div
                animate={{
                    y: [-15, 15, -15],
                    x: [-5, 5, -5]
                }}
                transition={{ duration: 7, repeat: Infinity, delay: 2 }}
                className="absolute bottom-32 left-20 hidden lg:block"
            >
                <Sparkles className="h-6 w-6 text-pink-400/70" />
            </motion.div>

            {/* Main Content */}
            <motion.section
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative z-10 mx-auto max-w-6xl w-full"
            >
                <motion.div
                    className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-lg rounded-3xl p-8 md:p-12 text-white shadow-2xl border border-white/20 overflow-hidden relative"
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-yellow-400 to-pink-400 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full blur-3xl"></div>
                    </div>

                    {/* Header */}
                    <motion.div
                        className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8"
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <div className="flex items-center mb-4 md:mb-0">
                            <motion.div
                                animate={{ rotate: [0, 10, 0, -10, 0] }}
                                transition={{ duration: 3, repeat: Infinity }}
                                className="mr-4"
                            >
                                <Sun className="h-10 w-10 text-amber-400" />
                            </motion.div>
                            <div>
                                <h3 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-amber-300 to-yellow-300 bg-clip-text text-transparent">
                                    Today's Declaration
                                </h3>
                                <p className="text-blue-200 text-sm mt-1">
                                    {new Date(todaysDeclaration.date).toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </p>
                            </div>
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleRefresh}
                            className="bg-gradient-to-r from-purple-600 to-pink-600 p-3 rounded-full shadow-lg hover:shadow-xl transition-shadow"
                        >
                            <RefreshCw className="h-6 w-6" />
                        </motion.button>
                    </motion.div>

                    {/* Declaration Content */}
                    <div className="space-y-8">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentDeclarationIndex}
                                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -30, scale: 0.95 }}
                                transition={{ duration: 0.5 }}
                                className="text-center"
                            >
                                <motion.blockquote
                                    className="text-xl md:text-3xl font-semibold leading-relaxed mb-6 relative"
                                    whileHover={{ scale: 1.02 }}
                                >
                                    <span className="bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                                        "{currentDeclaration}"
                                    </span>

                                    {/* Quotation marks */}
                                    <motion.span
                                        className="absolute -top-4 -left-2 text-6xl text-yellow-400/30"
                                        initial={{ rotate: -10 }}
                                        animate={{ rotate: 0 }}
                                        transition={{ delay: 0.2 }}
                                    >
                                        "
                                    </motion.span>
                                </motion.blockquote>
                            </motion.div>
                        </AnimatePresence>

                        {/* Declaration Navigation */}
                        <div className="flex justify-center items-center space-x-2 mb-6">
                            {todaysDeclaration.declarations.map((_, index) => (
                                <motion.button
                                    key={index}
                                    whileHover={{ scale: 1.2 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => setCurrentDeclarationIndex(index)}
                                    className={`w-3 h-3 rounded-full transition-all ${index === currentDeclarationIndex
                                        ? 'bg-yellow-400 shadow-lg shadow-yellow-400/50'
                                        : 'bg-white/30 hover:bg-white/50'
                                        }`}
                                />
                            ))}
                        </div>

                        {/* Bible Reference */}
                        <motion.div
                            className="bg-black/20 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center">
                                    <BookOpen className="h-6 w-6 text-amber-400 mr-3" />
                                    <h4 className="text-lg font-semibold text-amber-300">Scripture Reference</h4>
                                </div>
                                <motion.div
                                    animate={{ scale: [1, 1.1, 1] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    <Heart className="h-5 w-5 text-pink-400" />
                                </motion.div>
                            </div>
                            <p className="text-blue-100 text-sm leading-relaxed">{todaysDeclaration.bibleReference}</p>
                        </motion.div>

                        {/* Call to Action */}
                        <motion.div
                            className="text-center"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                        >
                            <motion.p
                                className="text-blue-200 mb-4 flex items-center justify-center"
                                animate={{ opacity: [0.7, 1, 0.7] }}
                                transition={{ duration: 3, repeat: Infinity }}
                            >
                                <Sparkles className="h-4 w-4 mr-2" />
                                Speak this truth over your life today
                                <ChevronRight className="h-4 w-4 ml-2" />
                            </motion.p>

                            <div className="text-sm text-white/60">
                                Declaration {currentDeclarationIndex + 1} of {todaysDeclaration.declarations.length}
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </motion.section>
        </main>
    );
};

export default DailyDeclarations;